<template>
  <i-dialog v-model="isOpen" width="450">
    <div v-if="!isSuccess" class="dialog-wrapper">
      <div class="dialog-close">
        <button class="close-btn" @pointerdown.prevent="closeDialog" @click="closeDialog">
          <v-icon :icon="CloseIcon" color="BW-800" size="24"/>
        </button>
      </div>
      <form class="dialog-container" @submit.prevent="handleSubmit">
        <div class="dialog-header">
          <p class="dialog-title">{{ t('title') }}</p>
          <p class="dialog-subtitle">{{ t('subtitle') }}</p>
        </div>
        <div class="dialog-content">
          <div class="dialog-field">
            <p class="dialog-field-label">{{ t('uin_label') }}</p>
            <uin-input v-model="uinRaw" :error="!!uinError" @focus="uinError = ''" @blur="checkUin" ref="uinInput"/>
            <p v-if="uinError" class="field-error-text">{{ uinError }}</p>
          </div>
          <ui-text-field v-model="fullName" :label="t('full_name_label')" :placeholder="t('full_name_placeholder')"
                         :error-message="fullNameError" @focus="fullNameError = ''" ref="nameInput"/>
          <ui-text-field
              :model-value="phoneDisplay"
              :label="t('phone_label')"
              :placeholder="t('phone_placeholder')"
              :error-message="phoneError"
              type="tel"
              @input="onPhoneInput"
              @keydown="onPhoneKeydown"
              @focus="onPhoneFocus"
              @paste="onPhonePaste"
              ref="phoneInput"
          />
        </div>
        <p v-if="isRateLimited" class="field-error-text rate-limit-error">{{ t('error_rate_limit') }}</p>
        <button type="submit" class="btn-primary btn-block" :disabled="isPending || isRateLimited">
          <v-progress-circular v-if="isPending" size="24" :width="2" indeterminate color="BW-450"
                               bg-color="transparent"/>
          <span>{{ t('submit') }}</span>
        </button>
      </form>
    </div>
    <div v-else class="dialog-wrapper">
      <div class="dialog-close">
        <button class="close-btn" @pointerdown.prevent="closeDialog" @click="closeDialog">
          <v-icon :icon="CloseIcon" color="BW-800" size="24"/>
        </button>
      </div>
      <div class="dialog-container">
        <div class="icon-container">
          <v-icon :icon="CheckIcon" color="BW-green-4" size="34"/>
        </div>
        <div class="dialog-header">
          <p class="dialog-title">{{ t('the_application_has_been_sent') }}</p>
          <p class="dialog-subtitle">{{ t('we_call_you_back_soon') }}</p>
        </div>
        <button class="btn-primary btn-block" @click="isOpen = false">
          <span>{{ t('close') }}</span>
        </button>
      </div>
    </div>
  </i-dialog>
</template>

<script setup lang="ts">
import {ref, watch, nextTick, useTemplateRef} from 'vue';
import {useI18n} from '@/shared/lib/i18n';
import {useMutation} from '@tanstack/vue-query';
import {storeToRefs} from 'pinia';
import {UiTextField} from '@ismet/vue-ui-kit';

import {UserModel} from '@/entities/User';

import IDialog from '@/shared/ui/IDialog.vue';
import UinInput from "@/shared/ui/UinInput.vue";

import CloseIcon from '@/shared/ui/icons/action/Close.vue';
import CheckIcon from '@/shared/ui/icons/status/CircleCheckboxMarked.vue'

import {requestDemo} from '../api';
import {BIN_PATTERN, checkIinOrBinCheckSum, IIN_PATTERN} from "@/shared/lib/utils/validators";

const {t} = useI18n();

const user = UserModel.useUserStore();

const {selectedContextCompany} = storeToRefs(user);
const {profile: userProfile} = UserModel.useUserProfile();

const isOpen = ref<boolean>(false);
const fullName = ref<string>('');
const phoneDisplay = ref<string>('');
const phoneRaw = ref<string>('');
const uinRaw = ref<string>('');

const fullNameError = ref<string>('');
const phoneError = ref<string>('');
const uinError = ref<string>('');
const isRateLimited = ref<boolean>(false);

const uinInputRef = useTemplateRef('uinInput');
const nameInputRef = useTemplateRef('nameInput');
const phoneInputRef = useTemplateRef('phoneInput');

const RATE_LIMIT_KEY = 'ask_demo_last_submitted';
const RATE_LIMIT_MS = 24 * 60 * 60 * 1000;

function checkRateLimit(): boolean {
  const last = localStorage.getItem(RATE_LIMIT_KEY);
  if (!last) return false;
  return Date.now() - parseInt(last) < RATE_LIMIT_MS;
}

const {mutate, isPending, isSuccess, reset} = useMutation({
  mutationFn: requestDemo,
  onSuccess: () => {
    localStorage.setItem(RATE_LIMIT_KEY, Date.now().toString());
    isRateLimited.value = true;
    fullName.value = '';
    phoneDisplay.value = '';
    phoneRaw.value = '';
  },
  onError: () => {
    phoneError.value = t('error_server');
  },
});

let pasted: boolean = false;

function applyMask(digits: string): string {
  let result = '+7 (';
  const d = digits.slice(0, 10);
  for (let i = 0; i < d.length; i++) {
    if (i === 3) result += ') ';
    else if (i === 6) result += ' ';
    else if (i === 8) result += ' ';
    result += d[i];
  }
  return result;
}

function onPhoneInput(e: Event) {
  if (pasted) {
    pasted = false;
    return
  }
  const input = e.target as HTMLInputElement;
  const digits = input.value.replace(/\D/g, '').replace(/^7/, '').slice(0, 10);
  phoneRaw.value = digits;
  const newDisplay = applyMask(digits);
  phoneDisplay.value = newDisplay;

  nextTick(() => {
    input.value = newDisplay;
  });
}

function onPhoneKeydown(e: KeyboardEvent) {
  if (e.key === 'Backspace') {
    phoneRaw.value = phoneRaw.value.slice(0, -1);
    phoneDisplay.value = applyMask(phoneRaw.value);
    e.preventDefault();
  }
}

function onPhoneFocus() {
  phoneError.value = '';
  if (!phoneDisplay.value) {
    phoneDisplay.value = '+7 (';
  }
}

function onPhonePaste(e: ClipboardEvent) {
  pasted = true

  const text = e.clipboardData?.getData('text')
  if (text) {
    const digits = text.replace(/\D/g, '').replace(/^[78]/, '').slice(0, 10);
    phoneRaw.value = digits;
    phoneDisplay.value = applyMask(digits);
  }
}

const checkUin = () => {
  const uin = uinRaw.value;
  if (uin.length !== 12 || (!IIN_PATTERN.test(uin) && !BIN_PATTERN.test(uin)) || !checkIinOrBinCheckSum(uin)) {
    uinError.value = t('invalid_uin');
  }
}

function closeDialog() {
  uinError.value = '';
  fullNameError.value = '';
  phoneError.value = '';
  isOpen.value = false;
}

function handleSubmit() {
  if (isRateLimited.value) return;

  const fullNameTrimmed = fullName.value.trim();
  fullNameError.value = fullNameTrimmed ? '' : t('error_full_name_required');
  phoneError.value = phoneRaw.value.length === 10 ? '' : t('error_phone');

  const uin = uinRaw.value;

  if (uin.length !== 12 || (!IIN_PATTERN.test(uin) && !BIN_PATTERN.test(uin)) || !checkIinOrBinCheckSum(uin)) {
    uinError.value = t('invalid_uin');
  }

  if (fullNameError.value || phoneError.value || uinError.value) return;

  mutate({phone: phoneRaw.value, fullName: fullNameTrimmed, uin});
}

watch(isOpen, (newVal) => {
  if (!newVal) {
    fullName.value = '';
    phoneDisplay.value = '';
    phoneRaw.value = '';
    uinRaw.value = '';
    uinError.value = '';
    fullNameError.value = '';
    phoneError.value = '';
  } else {
    reset();
    isRateLimited.value = checkRateLimit();
    fullName.value = userProfile.value?.fio ?? '';
    if (userProfile.value?.phone) {
      const digits = userProfile.value.phone.replace(/\D/g, '').replace(/^[78]/, '').slice(0, 10);
      phoneRaw.value = digits;
      phoneDisplay.value = applyMask(digits);
    }
    if (selectedContextCompany.value) {
      uinRaw.value = selectedContextCompany.value.bin;
      uinError.value = '';
    }

    nextTick().then(() => {
      if (!uinRaw.value) {
        uinInputRef.value?.focus();
      } else if (!fullName.value) {
        nameInputRef.value?.focus();
      } else if (!phoneDisplay.value) {
        phoneInputRef.value?.focus();
      }
    });
  }
}, {immediate: true});

watch(uinRaw, (uin) => {
  if (uin.length !== 12) return;
  if ((!IIN_PATTERN.test(uin) && !BIN_PATTERN.test(uin)) || !checkIinOrBinCheckSum(uin)) {
    uinError.value = t('invalid_uin');
  } else {
    uinError.value = '';
  }
}, {flush: 'post'});

defineExpose({
  open: () => {
    console.log('open demo dialog');
    isOpen.value = true;
  },
});
</script>

<style scoped>
.dialog-wrapper {
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 1.5rem 1.5rem 2rem;
  border-radius: .5rem;
  box-shadow: 0 0 0 1px #F5F5F5, 0 7px 16px 0 rgba(19, 32, 77, 0.07);
}

.dialog-close {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.dialog-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.dialog-header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: center;
}

.dialog-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.75rem;
}

.dialog-subtitle {
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 400;
  color: var(--BW-700);
}

.dialog-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.dialog-field {
  display: flex;
  flex-direction: column;
  gap: .25rem;
}

.dialog-field-label {
  font-size: .875rem;
  line-height: 1.25rem;
  color: var(--BW-700);
}

.icon-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.field-error-text {
  font-size: 12px;
  color: var(--BW-red-6);
  line-height: 1rem;
  margin-top: 0.25rem;
}

.rate-limit-error {
  text-align: center;
  margin-top: 0;
}
</style>

<i18n lang="json">
{
  "ru": {
    "title": "Запрос демонстрации",
    "subtitle": "Оставьте свои контакты, и мы свяжемся с вами для проведения демонстрации",
    "full_name_label": "Имя и Фамилия",
    "full_name_placeholder": "Введите имя и фамилию",
    "phone_label": "Номер телефона",
    "phone_placeholder": "+7 (702) 777 77 77",
    "submit": "Запросить демонстрацию",
    "error_full_name_required": "Укажите имя и фамилию",
    "error_phone": "Укажите номер телефона",
    "error_server": "Ошибка отправки, попробуйте позже",
    "error_rate_limit": "Вы уже отправили заявку сегодня. Попробуйте снова завтра.",
    "the_application_has_been_sent": "Заявка отправлена",
    "we_call_you_back_soon": "Свяжемся в ближайшее время и подтвердим детали",
    "uin_label": "БИН/ИИН",
    "invalid_uin": "Неверный формат БИН/ИИН"
  },
  "kk": {
    "title": "Демонстрация сұрау",
    "subtitle": "Байланыс деректеріңізді қалдырыңыз, біз сізбен хабарласамыз",
    "full_name_label": "Аты және Тегі",
    "full_name_placeholder": "Атыңызды және тегіңізді енгізіңіз",
    "phone_label": "Телефон нөмірі",
    "phone_placeholder": "+7 (702) 777 77 77",
    "submit": "Демонстрация сұрау",
    "error_full_name_required": "Атыңызды және тегіңізді көрсетіңіз",
    "error_phone": "Телефон нөмірін көрсетіңіз",
    "error_server": "Жіберу қатесі, кейінірек көріңіз",
    "error_rate_limit": "Сіз бүгін өтінім жібердіңіз. Ертең қайта көріңіз.",
    "the_application_has_been_sent": "Өтініш жіберілді",
    "we_call_you_back_soon": "Біз жақын арада хабарласып, егжей-тегжейін растаңыз",
    "uin_label": "БСН/ЖСН",
    "invalid_uin": "БСН/ЖСН дұрыс емес форматы"
  },
  "en": {
    "title": "Ask for demo",
    "subtitle": "Leave your contacts and we will get in touch to schedule a demo",
    "full_name_label": "Full name",
    "full_name_placeholder": "Enter your full name",
    "phone_label": "Phone number",
    "phone_placeholder": "+7 (702) 777 77 77",
    "submit": "Request a demo",
    "error_full_name_required": "Please enter your full name",
    "error_phone": "Please enter a valid phone number",
    "error_server": "Submission error, please try again later",
    "error_rate_limit": "You have already submitted a request today. Please try again tomorrow.",
    "the_application_has_been_sent": "The application has been sent",
    "we_call_you_back_soon": "We will get in touch soon and confirm the details",
    "uin_label": "BIN/IIN",
    "invalid_uin": "The BIN/IIN is invalid"
  }
}
</i18n>
